import * as com from "../Common"

// Section 9 of the 1003 (Loan Originator Information) is prefilled from the
// loan officer's profile and the company profile (Management > Company
// Profile). The company profile page keeps its address in two places at once:
// the one-line "Company address" box (Address) and its own State, City and Zip
// boxes. Typed by hand, Address is only the street ("1234 Main St"); picked
// from the address suggestions it is "1234 Main St, Phoenix, AZ 85004".
//
// The 1003 used to read Address alone and split it on commas. A hand-typed
// street therefore prefilled no address at all, and a picked one lost the state
// and put "AZ" in the zip (" AZ 85004" split on spaces starts with ""), while
// the NMLS and license numbers, read from their own keys, always came through.

const text = (v) => `${v || ""}`.trim()

const COUNTRY = /^(us|usa|united states( of america)?)$/i
const ZIP = /^\d{5}(-\d{4})?$/
const STATE_AND_ZIP = /^(.*?)\s*(\d{5}(?:-\d{4})?)?$/

// companyAddressForBroker returns the company address as Section 9 stores it:
// street, city, state (full name, as the 1003 state list uses) and zip. The
// profile's own City, State and Zip boxes win over what the one-line address
// says; the one-line address is only parsed for what those boxes leave empty.
export const companyAddressForBroker = (profile) => {
    const p = profile || {}
    const parts = text(p.Address).split(",").map((s) => s.trim()).filter(Boolean)
    if (parts.length > 1 && COUNTRY.test(parts[parts.length - 1])) parts.pop()

    let street = parts.join(", ")
    let city = ""
    let state = ""
    let zip = ""
    const n = parts.length
    if (n >= 4 && ZIP.test(parts[n - 1])) {
        // "street, city, state, zip"
        street = parts.slice(0, n - 3).join(", ")
        city = parts[n - 3]
        state = parts[n - 2]
        zip = parts[n - 1]
    } else if (n >= 3) {
        // "street, city, ST zip" -- the format the address suggestions write
        const m = parts[n - 1].match(STATE_AND_ZIP)
        street = parts.slice(0, n - 2).join(", ")
        city = parts[n - 2]
        state = m ? m[1] : parts[n - 1]
        zip = m && m[2] ? m[2] : ""
    }

    const ownCity = text(p.City)
    if (ownCity) {
        // The street is whatever the one-line address has before the city.
        const at = parts.findIndex((s, i) => i > 0 && s.toLowerCase() === ownCity.toLowerCase())
        if (at > 0) street = parts.slice(0, at).join(", ")
        city = ownCity
    }
    if (text(p.State)) state = text(p.State)
    if (text(p.Zip)) zip = text(p.Zip)

    return {
        street,
        city,
        state: state ? com.capitalize(com.convertAbbStateToFull(state)) : "",
        zip,
    }
}

// The old prefill could leave a state code ("AZ") in the zip. No zip is two
// letters, so such a value is treated as empty and the company zip replaces it.
const leftoverFromOldSeed = (key, value) => key === "zip" && /^[A-Za-z]{2}$/.test(value)

// mergeBrokerSeed fills Section 9 from the profiles without overwriting what
// the loan officer typed: a field is filled when it is empty, or re-applied
// when it already equals the profile value.
export const mergeBrokerSeed = (current, seed) => {
    const next = { ...(current || {}) }
    let changed = false
    Object.keys(seed || {}).forEach((key) => {
        const cur = `${next[key] || ""}`.trim()
        const nxt = `${seed[key] || ""}`.trim()
        if (!nxt) return
        if (cur === "" || cur === nxt || leftoverFromOldSeed(key, cur)) {
            if (next[key] !== seed[key]) {
                next[key] = seed[key]
                changed = true
            }
        }
    })
    return { next, changed }
}
